// atoms.jsx — shared mobile UI primitives used by all variants.
// Ported from /tmp/handoff-fcc-weather/src/atoms.jsx — React hooks
// converted to Preact equivalents, window.WXA globals → ES exports.

import { useRef, useState, useEffect, useLayoutEffect } from 'preact/hooks';
import { WxIcon, Glyph } from './icons.jsx';
import { getAuth, authReturnUrl } from './data-bridge.js';

// ─── useTap micro-animation hook ────────────────────────────────────────────
export function useTap() {
  const ref = useRef(null);
  const trigger = () => {
    const el = ref.current; if (!el) return;
    el.classList.remove('wx-tap-fire');
    void el.offsetWidth;
    el.classList.add('wx-tap-fire');
  };
  return { ref, trigger };
}

// ─── Long-press tooltip (380ms hold by default) ─────────────────────────────
// Cancels on pointermove too — otherwise starting a long-press on a metric
// chip and then moving the finger (i.e. scrolling) keeps the timer alive
// and fires the tooltip mid-scroll, feeling "sticky".
export function useLongPress(onLong, ms = 380) {
  const tRef = useRef(null);
  const startRef = useRef(null);
  const MOVE_THRESH = 8; // px before we count it as a drag, not a hold
  const cancel = () => { if (tRef.current) { clearTimeout(tRef.current); tRef.current = null; } startRef.current = null; };
  return {
    onPointerDown: (e) => { cancel(); startRef.current = { x: e.clientX, y: e.clientY }; tRef.current = setTimeout(onLong, ms); },
    onPointerMove: (e) => {
      const s = startRef.current; if (!s) return;
      if (Math.abs(e.clientX - s.x) > MOVE_THRESH || Math.abs(e.clientY - s.y) > MOVE_THRESH) cancel();
    },
    onPointerUp:    cancel,
    onPointerLeave: cancel,
    onPointerCancel:cancel,
  };
}

// ─── Pull-to-refresh wrapper ─────────────────────────────────────────────────
// iOS Safari registers touchmove with { passive: true } by default for any
// listener attached via JSX/React props, so e.preventDefault() in those handlers
// is silently ignored — the browser keeps scrolling and our pull gesture never
// captures the drag. Fix: attach touchstart/touchmove/touchend manually via
// useEffect + addEventListener with { passive: false } so we own the gesture.
export function PullToRefresh({ children, onRefresh, brand }) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const scrollRef = useRef(null);
  const startY = useRef(null);
  const pullRef = useRef(0);
  const refreshingRef = useRef(false);
  const THRESH = 70;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onStart = (e) => {
      if (refreshingRef.current) return;
      if (el.scrollTop <= 0) {
        startY.current = e.touches ? e.touches[0].clientY : e.clientY;
      } else {
        startY.current = null;
      }
    };
    const onMove = (e) => {
      if (startY.current == null) return;
      if (el.scrollTop > 0) { startY.current = null; setPull(0); pullRef.current = 0; return; }
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      const dy = y - startY.current;
      if (dy > 0) {
        const d = Math.min(dy, THRESH * 1.6);
        const p = d / THRESH;
        setPull(p);
        pullRef.current = p;
        if (e.cancelable) e.preventDefault();
      }
    };
    const onEnd = () => {
      if (startY.current == null) return;
      if (pullRef.current >= 1 && !refreshingRef.current) {
        setRefreshing(true); refreshingRef.current = true;
        setPull(1); pullRef.current = 1;
        Promise.resolve(onRefresh?.()).finally(() => {
          setTimeout(() => {
            setRefreshing(false); refreshingRef.current = false;
            setPull(0); pullRef.current = 0;
          }, 600);
        });
      } else {
        setPull(0); pullRef.current = 0;
      }
      startY.current = null;
    };

    // passive:false on touchmove is the whole reason this useEffect exists.
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove',  onMove,  { passive: false });
    el.addEventListener('touchend',   onEnd,   { passive: true });
    el.addEventListener('touchcancel',onEnd,   { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove',  onMove);
      el.removeEventListener('touchend',   onEnd);
      el.removeEventListener('touchcancel',onEnd);
    };
  }, [onRefresh]);

  const showPull = pull > 0 || refreshing;
  const headerH = refreshing ? 56 : Math.min(pull * 56, 64);

  return (
    <div style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: headerH,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: brand.textDim, fontSize: 12, letterSpacing: '.05em',
        textTransform: 'uppercase', opacity: showPull ? 1 : 0,
        transition: refreshing ? 'height .2s' : 'opacity .12s',
        background: brand.bg,
        zIndex: 1,
      }}>
        <div style={{
          width: 22, height: 22, borderRadius: '50%',
          border: `2px solid ${brand.line}`, borderTopColor: brand.accent,
          marginRight: 10,
          animation: refreshing ? 'wx-spin 0.9s linear infinite' : 'none',
          transform: refreshing ? 'none' : `rotate(${pull * 270}deg)`,
        }}/>
        <span style={{ fontFamily: brand.eyebrowFont }}>
          {refreshing ? 'Refreshing…' : pull >= 1 ? 'Release to refresh' : 'Pull to refresh'}
        </span>
      </div>
      <div
        ref={scrollRef}
        style={{
          height: '100%', overflowY: 'auto', overflowX: 'hidden',
          transform: `translateY(${headerH}px)`,
          transition: refreshing || pull === 0 ? 'transform .2s' : 'none',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ─── Collapsing header (compact on scroll) ───────────────────────────────────
export function CollapsingHeader({ scrollRef, brand, location, temp, cond, onSearch }) {
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    const el = scrollRef?.current; if (!el) return;
    const handler = () => setCollapsed(el.scrollTop > 60);
    el.addEventListener('scroll', handler, { passive: true });
    return () => el.removeEventListener('scroll', handler);
  }, [scrollRef]);
  return (
    <div style={{
      position: 'sticky', top: 58, zIndex: 10, padding: '8px 16px',
      background: collapsed ? brand.bg + 'ee' : 'transparent',
      backdropFilter: collapsed ? 'blur(20px) saturate(140%)' : 'none',
      WebkitBackdropFilter: collapsed ? 'blur(20px) saturate(140%)' : 'none',
      borderBottom: collapsed ? `1px solid ${brand.line}` : '1px solid transparent',
      transition: 'background .2s, border-color .2s',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
    }}>
      <div style={{ minWidth: 0, flex: 1, opacity: collapsed ? 1 : 0, transition: 'opacity .25s' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: brand.text, lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{location}</div>
        <div style={{ fontSize: 11, color: brand.textDim, lineHeight: 1.1, marginTop: 2 }}>{temp}° · {cond}</div>
      </div>
      <button type="button" onClick={onSearch} className="wx-tap" aria-label="Search locations" style={{
        width: 36, height: 36, borderRadius: 18, border: `1px solid ${brand.line}`,
        background: brand.panel, color: brand.text, display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Glyph.Search s={16}/>
      </button>
    </div>
  );
}

// ─── Bottom sheet (with backdrop) ────────────────────────────────────────────
export function BottomSheet({ open, onClose, title, children, brand, height = '70%' }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const dialog = ref.current;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  // Native modality contains keyboard focus and excludes the background from
  // assistive technology. A closed dialog has no offscreen tabbable controls.
  const closeFromBackdrop = (event) => {
    if (event.target !== event.currentTarget) return;
    const box = event.currentTarget.getBoundingClientRect();
    if (event.clientX < box.left || event.clientX > box.right ||
        event.clientY < box.top || event.clientY > box.bottom) onClose();
  };
  return (
      <dialog ref={ref} className="wx-bottom-sheet" aria-label={title}
        onCancel={(event) => { event.preventDefault(); onClose(); }}
        onClose={() => { if (!ref.current?.open) onClose(); }}
        onClick={closeFromBackdrop} style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, top: 'auto', height,
        width: '100%', maxWidth: 'none', maxHeight: '100%', margin: 0, padding: 0,
        background: brand.panel, color: brand.text,
        borderTopLeftRadius: 22, borderTopRightRadius: 22,
        border: 'none',
        borderTop: `1px solid ${brand.line}`,
        display: open ? 'flex' : 'none', flexDirection: 'column',
        boxShadow: '0 -20px 60px rgba(0,0,0,.6)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: brand.border, opacity: .8 }}/>
        </div>
        {title && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '6px 18px 12px', borderBottom: `1px solid ${brand.line}`,
          }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{title}</h3>
            <button type="button" onClick={onClose} style={{
              background: 'transparent', border: 'none', color: brand.textDim,
              fontSize: 15, padding: 4,
            }}>Done</button>
          </div>
        )}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '4px 0' }}>
          {children}
        </div>
      </dialog>
  );
}

// ─── Long-press tooltip toaster ─────────────────────────────────────────────
export function MetricTooltip({ tip, anchor, onClose, brand }) {
  if (!tip) return null;
  return (
    <div onClick={onClose} style={{
      position: 'absolute', inset: 0, zIndex: 90,
      background: 'rgba(0,0,0,.35)', display: 'flex', alignItems: 'flex-end',
      padding: 16,
    }}>
      <div style={{
        background: brand.panelHi, color: brand.text, padding: '14px 16px',
        borderRadius: 14, border: `1px solid ${brand.line}`,
        boxShadow: '0 16px 40px rgba(0,0,0,.5)', maxWidth: '100%',
      }}>
        <div style={{ fontSize: 11, fontFamily: brand.eyebrowFont, color: brand.accent, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>{anchor}</div>
        <div style={{ fontSize: 14, lineHeight: 1.45 }}>{tip}</div>
      </div>
    </div>
  );
}

// ─── Day row (10-day) — tap to expand inline hourly drilldown ────────────────
export function DayRow({ row, idx, max, min, brand, density, expanded, onToggle }) {
  const tap = useTap();
  const isToday = row.day === 'Today';
  const span = Math.max(1, max - min);
  const lpct = ((row.lo - min) / span) * 100;
  const wpct = ((row.hi - row.lo) / span) * 100;
  return (
    <div ref={tap.ref} className="wx-tap wx-row" onClick={() => { tap.trigger(); onToggle?.(); }}
      style={{
        padding: `${density.gap}px ${density.pad}px`,
        borderBottom: `1px solid ${brand.line}`,
        cursor: 'pointer', position: 'relative',
      }}>
      <div style={{ display: 'grid', gridTemplateColumns: '48px 36px 1fr 60px', alignItems: 'center', gap: 10 }}>
        <div style={{ fontWeight: isToday ? 700 : 500, fontSize: 15 * density.fs, color: brand.text }}>{row.day}</div>
        <WxIcon kind={row.icon} size={28}/>
        <div style={{ position: 'relative', height: 8, background: 'rgba(255,255,255,.05)', borderRadius: 4 }}>
          <div style={{
            position: 'absolute', top: 0, bottom: 0, left: `${lpct}%`, width: `${wpct}%`,
            background: `linear-gradient(90deg, ${brand.accentDeep}, ${brand.accent})`,
            borderRadius: 4,
          }}/>
          {row.pop > 30 && (
            <div style={{
              position: 'absolute', top: -16, left: `${lpct + wpct/2}%`, transform: 'translateX(-50%)',
              fontSize: 10, color: '#60a5fa', display: 'flex', alignItems: 'center', gap: 2,
              fontFamily: brand.eyebrowFont,
            }}><Glyph.Drop s={9} c="#60a5fa"/> {row.pop}%</div>
          )}
        </div>
        <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 14 * density.fs, color: brand.text }}>
          <span style={{ color: brand.textDim }}>{row.lo}°</span>
          <span style={{ margin: '0 4px', color: brand.border }}>·</span>
          <span style={{ fontWeight: 600 }}>{row.hi}°</span>
        </div>
      </div>
      {expanded && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px dashed ${brand.line}`, fontSize: 13, color: brand.textDim, lineHeight: 1.5 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 8 }}>
            {[{k:'Hi', v: row.hi+'°'},{k:'Lo', v: row.lo+'°'},{k:'Precip', v: row.pop+'%'},{k:'Wind', v: '8 mph'}].map(m => (
              <div key={m.k}>
                <div style={{ fontSize: 10, color: brand.textDim, textTransform: 'uppercase', letterSpacing: '.06em', fontFamily: brand.eyebrowFont }}>{m.k}</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: brand.text }}>{m.v}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 13, color: brand.text }}>{row.cond}. {row.pop > 50 ? 'Bring an umbrella.' : row.pop > 20 ? 'Slight chance of precip.' : 'No precipitation expected.'}</div>
        </div>
      )}
    </div>
  );
}

// ─── Hour cell (horizontal strip) ────────────────────────────────────────────
export function HourCell({ hr, brand, density, peak }) {
  return (
    <div style={{
      minWidth: density.hourW, display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 6, padding: `${density.gap}px 4px`,
      borderRight: `1px solid ${brand.line}`,
    }}>
      <div style={{ fontSize: 11, color: brand.textDim, fontFamily: brand.eyebrowFont, textTransform: 'uppercase', letterSpacing: '.05em' }}>{hr.time}</div>
      <div style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <WxIcon kind={hr.icon} size={34}/>
      </div>
      {hr.pop > 5 ? (
        <div style={{ fontSize: 10, color: '#60a5fa', display: 'flex', alignItems: 'center', gap: 2 }}>
          <Glyph.Drop s={9} c="#60a5fa"/> {hr.pop}%
        </div>
      ) : <div style={{ height: 13 }}/>}
      <div style={{ fontSize: 15, fontWeight: peak ? 700 : 500, color: brand.text, fontVariantNumeric: 'tabular-nums' }}>{hr.temp}°</div>
    </div>
  );
}

// ─── Metric chip (with long-press tooltip) ───────────────────────────────────
export function MetricChip({ m, brand, density, onLongPress }) {
  const lp = useLongPress(() => onLongPress?.(m), 380);
  return (
    <div {...lp} className="wx-tap" style={{
      background: brand.panel, border: `1px solid ${brand.line}`, borderRadius: 12,
      padding: `${density.gap - 2}px ${density.pad - 2}px`,
      userSelect: 'none', cursor: 'help', minWidth: 0,
    }}>
      <div style={{ fontSize: 10 * density.fs, color: brand.textDim, textTransform: 'uppercase', letterSpacing: '.06em', fontFamily: brand.eyebrowFont, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.k}</div>
      <div style={{ fontSize: 22 * density.fs, fontWeight: 700, color: brand.text, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1, marginTop: 2 }}>{m.v}</div>
      {m.sub && <div style={{ fontSize: 11 * density.fs, color: brand.textDim, marginTop: 1 }}>{m.sub}</div>}
    </div>
  );
}

// ─── Section header ─────────────────────────────────────────────────────────
export function SectionHead({ num, title, right, brand, density }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      padding: `${density.gap + 6}px ${density.pad}px ${density.gap}px`,
      gap: 12,
    }}>
      <div style={{ minWidth: 0 }}>
        {num && <div style={{ fontSize: 10, color: brand.textDim, fontFamily: brand.eyebrowFont, textTransform: 'uppercase', letterSpacing: '.08em' }}>{num}</div>}
        <h3 style={{ margin: '2px 0 0', fontSize: 18 * density.fs, fontWeight: 600, color: brand.text, letterSpacing: '-.01em' }}>{title}</h3>
      </div>
      {right && <div style={{ fontSize: 11, color: brand.textDim, fontFamily: brand.eyebrowFont, textAlign: 'right', flexShrink: 0 }}>{right}</div>}
    </div>
  );
}

// ─── Brand wordmark (icon + name) ───────────────────────────────────────────
export function Wordmark({ brand, size = 16 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: brand.accent }}>
      {brand.icon === 'paw' ? <Glyph.Paw s={size + 4} c={brand.accent}/> : <Glyph.FCC s={size + 2} c={brand.accent}/>}
      <span style={{ fontFamily: brand.eyebrowFont, fontSize: size, fontWeight: 600, letterSpacing: brand.icon === 'paw' ? '.02em' : '-.01em' }}>{brand.wordmark}</span>
    </div>
  );
}

// ─── Auth footer — sign-in entry or signed-in label ──────────────────────
// Reads STATE.auth from data-bridge (populated by /api/auth/me at boot).
// When authenticated: shows "Signed in as <name>" + Sign out.
// When anonymous: shows a "Sign in to fcc.cc" link to /account/.
export function AuthFooter({ brand, density }) {
  const auth = getAuth();
  if (!auth.loaded) return null; // wait one tick — avoid flashing the wrong state
  const pad = `${density.gap + 4}px ${density.pad}px ${density.gap + 4}px`;

  if (auth.authenticated && auth.user) {
    const name = auth.user.display_name || auth.user.username || auth.user.email || 'your account';
    return (
      <div style={{
        padding: pad, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, borderTop: `1px solid ${brand.line}`, color: brand.textDim, fontSize: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span aria-hidden="true" style={{
            width: 22, height: 22, borderRadius: 11, background: brand.accent, color: brand.bg,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
            flexShrink: 0,
          }}>{String(name).trim().charAt(0).toUpperCase() || '·'}</span>
          <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Signed in as <span style={{ color: brand.text, fontWeight: 600 }}>{name}</span>
          </div>
        </div>
        <a href="/account/" style={{
          color: brand.accent, textDecoration: 'none', fontSize: 12, fontFamily: brand.eyebrowFont,
          fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em',
        }}>Account</a>
      </div>
    );
  }

  return (
    <div style={{
      padding: pad, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, borderTop: `1px solid ${brand.line}`, color: brand.textDim, fontSize: 12,
    }}>
      <div style={{ minWidth: 0 }}>
        Sign in to keep favorites across devices.
      </div>
      <a href={authReturnUrl()} className="wx-tap" style={{
        padding: '8px 14px', borderRadius: 999, background: brand.accent, color: brand.bg,
        border: 'none', fontSize: 12, fontFamily: brand.eyebrowFont, fontWeight: 700,
        textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '.06em',
        flexShrink: 0, cursor: 'pointer',
      }}>Sign in</a>
    </div>
  );
}

// ─── Alert banner ───────────────────────────────────────────────────────────
export function AlertBanner({ alert, brand, density, compact }) {
  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(251,191,36,.15), rgba(251,146,60,.1))',
      border: `1px solid rgba(251,191,36,.35)`, borderLeft: '3px solid #fbbf24',
      borderRadius: 12, padding: `${density.gap}px ${density.pad}px`,
      margin: `0 ${density.pad}px ${density.gap}px`,
      display: 'flex', gap: 12, alignItems: 'flex-start',
    }}>
      <Glyph.Warn s={20}/>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: '#fbbf24', fontFamily: brand.eyebrowFont, textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>{alert.severity} · NWS Alert</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: brand.text, marginTop: 1 }}>{alert.head}</div>
        {!compact && <div style={{ fontSize: 12.5, color: brand.textDim, marginTop: 4, lineHeight: 1.45 }}>{alert.body}</div>}
      </div>
    </div>
  );
}
